import { useEffect, useState } from "react";
import { adminCsrfHeaders } from "../lib/adminApi";
import { ShieldAlert } from "lucide-react";
import { queryClient } from "../lib/queryClient";
import { useOrg } from "../lib/orgContext";

export function ImpersonationBanner() {
  const [orgId, setOrgId] = useState<number | null>(null);
  const [orgName, setOrgName] = useState<string | null>(null);
  const [mode, setMode] = useState<"read_only" | "full_access">("read_only");
  const [stopping, setStopping] = useState(false);
  const { refetch: refetchOrg } = useOrg();

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch("/api/healthz", { credentials: "include" });
        const header = res.headers.get("X-Owner-Impersonating");
        if (cancelled || !header) return;
        setOrgId(parseInt(header, 10));
        const modeHeader = res.headers.get("X-Owner-Impersonation-Mode");
        if (modeHeader === "full_access" || modeHeader === "read_only") {
          setMode(modeHeader);
        }
        // Force OrgProvider to re-pull /auth/org so any consumer of useOrg
        // (sidebar plan badge, useFeature, persona-gated UI) immediately
        // sees the impersonated tenant's plan/personas instead of whatever
        // was cached from the owner's own session (task #453).
        refetchOrg();
        // Surface the target org's name so the owner can visually confirm
        // they landed on the right tenant (task #453).
        try {
          const orgRes = await fetch("/api/auth/org", { credentials: "include" });
          if (orgRes.ok) {
            const data = (await orgRes.json()) as { name?: string; institutionName?: string | null };
            if (!cancelled) setOrgName(data.institutionName ?? data.name ?? null);
          }
        } catch {/* ignore */}
      } catch {/* ignore */}
    })();
    return () => { cancelled = true; };
  }, [refetchOrg]);

  if (orgId === null) return null;

  async function handleStop() {
    setStopping(true);
    const targetOrgId = orgId;
    try {
      await fetch("/api/admin/impersonate/stop", {
        method: "POST",
        credentials: "include",
        headers: adminCsrfHeaders(),
      });
    } finally {
      // Drop all React Query data so the impersonated tenant's lists do not
      // flash on the owner's own next view (task #453).
      queryClient.clear();
      // Send the owner back to the org detail page they were investigating,
      // not the in-app surface they were impersonating into. Reloading the
      // current page would dump them on the tenant's dashboard with no
      // session and force them to navigate back manually.
      const base = import.meta.env.BASE_URL.replace(/\/$/, "");
      window.location.href = targetOrgId !== null
        ? `${base}/admin/orgs/${targetOrgId}`
        : `${base}/admin`;
    }
  }

  const isFull = mode === "full_access";
  const bg = isFull ? "bg-red-800" : "bg-red-700";
  return (
    <div
      className={`${bg} text-white text-xs sm:text-sm px-4 py-2 flex items-center justify-center gap-3 sticky top-0 z-50 shadow`}
      data-testid="impersonation-banner"
      data-mode={mode}
    >
      <ShieldAlert className="h-4 w-4 flex-shrink-0" />
      <span>
        <strong>
          Owner impersonation ({isFull ? "full access — changes are real" : "read-only"})
        </strong>{" "}
        — viewing{" "}
        {orgName ? <strong data-testid="impersonation-org-name">{orgName}</strong> : "this org"}{" "}
        (org #{orgId}).{" "}
        {isFull
          ? "Every write is audited as you, attributed back to this tenant."
          : "Any attempt to modify data will be rejected."}
      </span>
      <button
        onClick={handleStop}
        disabled={stopping}
        className="ml-2 px-3 py-1 rounded bg-white/15 hover:bg-white/25 disabled:opacity-60 transition-colors"
        data-testid="impersonation-stop"
      >
        {stopping ? "Stopping…" : "Stop impersonating"}
      </button>
    </div>
  );
}
